/**
 * Smart Widget Configuration
 * Defines states, dimensions, and breakpoints for the chat widget
 */

// Widget States
export const WIDGET_STATES = {
  COLLAPSED: 'collapsed',
  HALF: 'half',
  FULL: 'full',
};

// Device Types
export const DEVICE_TYPES = {
  MOBILE: 'mobile',
  TABLET: 'tablet',
  DESKTOP: 'desktop',
};

// Breakpoints
export const BREAKPOINTS = {
  MOBILE: 480,
  TABLET: 768,
  DESKTOP: 1024,
};

// Widget Dimensions per state and device
export const WIDGET_DIMENSIONS = {
  [DEVICE_TYPES.MOBILE]: {
    [WIDGET_STATES.COLLAPSED]: { width: '70px', height: '70px' },
    [WIDGET_STATES.HALF]: { width: '100vw', height: '50dvh' },
    [WIDGET_STATES.FULL]: { width: '100vw', height: '100dvh' },
  },
  [DEVICE_TYPES.TABLET]: {
    [WIDGET_STATES.COLLAPSED]: { width: '80px', height: '80px' },
    [WIDGET_STATES.HALF]: { width: '380px', height: '50vh' },
    [WIDGET_STATES.FULL]: { width: '100vw', height: '85vh' },
  },
  [DEVICE_TYPES.DESKTOP]: {
    [WIDGET_STATES.COLLAPSED]: { width: '350px', height: '160px' },
    [WIDGET_STATES.HALF]: { width: '400px', height: '50vh' },
    [WIDGET_STATES.FULL]: { width: '400px', height: '70vh' },
  },
};

// Fallback dimensions (for older browsers without dvh support)
export const WIDGET_DIMENSIONS_FALLBACK = {
  [DEVICE_TYPES.MOBILE]: {
    [WIDGET_STATES.HALF]: { width: '100vw', height: '50vh' },
    [WIDGET_STATES.FULL]: { width: '100vw', height: '100vh' },
  },
  [DEVICE_TYPES.TABLET]: {
    [WIDGET_STATES.HALF]: { width: '380px', height: '50vh' },
    [WIDGET_STATES.FULL]: { width: '100vw', height: '85vh' },
  },
  [DEVICE_TYPES.DESKTOP]: {
    [WIDGET_STATES.HALF]: { width: '400px', height: '50vh' },
    [WIDGET_STATES.FULL]: { width: '400px', height: '70vh' },
  },
};

// The width of the browser window the widget is embedded in, which is not the
// same as its own. Set from the windowSize the SDK reports.
let hostViewportWidth = null;

export const setHostViewportWidth = (width) => {
  const parsed = parseFloat(width);
  hostViewportWidth = Number.isNaN(parsed) || parsed <= 0 ? null : parsed;
};

/**
 * Detect device type based on screen width
 */
export const getDeviceType = () => {
  // window.innerWidth inside the frame is the frame's own width, and the frame
  // is a 60px launcher until the moment it opens. Measuring that made every
  // desktop look like a phone, so the widget opened full-screen and swallowed
  // the shop it was supposed to sit beside.
  const width = hostViewportWidth || window.innerWidth;
  if (width <= BREAKPOINTS.MOBILE) return DEVICE_TYPES.MOBILE;
  if (width <= BREAKPOINTS.TABLET) return DEVICE_TYPES.TABLET;
  return DEVICE_TYPES.DESKTOP;
};

/**
 * Check if browser supports dvh units
 */
export const supportsDvh = () => {
  try {
    return CSS.supports('height', '100dvh');
  } catch {
    return false;
  }
};

/**
 * Get widget dimensions for current state and device
 */
export const getWidgetDimensions = (state, deviceType = null) => {
  const device = deviceType || getDeviceType();
  const hasDvhSupport = supportsDvh();

  // Use fallback dimensions if dvh not supported
  if (!hasDvhSupport && WIDGET_DIMENSIONS_FALLBACK[device]?.[state]) {
    return WIDGET_DIMENSIONS_FALLBACK[device][state];
  }

  return WIDGET_DIMENSIONS[device]?.[state] || WIDGET_DIMENSIONS[DEVICE_TYPES.DESKTOP][state];
};

/**
 * Get safe area insets for notched devices
 */
export const getSafeAreaInsets = () => {
  const computedStyle = getComputedStyle(document.documentElement);
  return {
    top: computedStyle.getPropertyValue('--sat') || '0px',
    right: computedStyle.getPropertyValue('--sar') || '0px',
    bottom: computedStyle.getPropertyValue('--sab') || '0px',
    left: computedStyle.getPropertyValue('--sal') || '0px',
  };
};

/**
 * Message types for parent-iframe communication
 */
export const MESSAGE_TYPES = {
  INIT: 'init',
  DIMENSIONS: 'dimensions',
  STATE_CHANGE: 'stateChange',
  ORG: 'org',
  INVALID_ORG: 'invalidOrg',
  SET_PARENT_STYLE: 'setParentStyle',
  RESET_PARENT_STYLE: 'resetParentStyle',
  FLOATING_WIDGET_STYLE: 'floatingWidgetStyle',
  RESET_FLOATING_WIDGET_STYLE: 'resetFloatingWidgetStyle',
  WINDOW_SIZE: 'windowSize',
};

// Embedded on a shop, i.e. the SDK has told us how big the window is. A
// standalone load never receives that message.
export const isEmbedded = () => hostViewportWidth !== null;

const fullViewportHeight = () => (supportsDvh() ? '100dvh' : '100vh');

/**
 * Get the open (expanded) width for the current device
 *
 * Embedded on a shop the open frame is always the whole viewport. The frame
 * is transparent; the panel inside it is laid out by this app's own styles,
 * a 400px rounded panel with its shadow on a desktop, edge to edge on a
 * phone. Sizing the frame to the panel instead put the panel in a 400px
 * viewport, where those same styles take it for a phone and draw it square,
 * flush and without its shadow.
 *
 * Standalone: Mobile/Tablet full viewport width; Desktop fixed 400px.
 */
export const getResponsiveOpenWidth = () => {
  if (isEmbedded()) return '100vw';
  const device = getDeviceType();
  if (device === DEVICE_TYPES.DESKTOP) return '400px';
  return '100vw';
};

/**
 * Get the open (expanded) height for the current device
 *
 * Embedded: the whole viewport, see getResponsiveOpenWidth.
 * Standalone: Mobile full viewport height; Tablet 85vh; Desktop 70vh.
 */
export const getResponsiveOpenHeight = () => {
  if (isEmbedded()) return fullViewportHeight();
  const device = getDeviceType();
  if (device === DEVICE_TYPES.MOBILE) return fullViewportHeight();
  if (device === DEVICE_TYPES.TABLET) return '85vh';
  return '70vh';
};

export default {
  WIDGET_STATES,
  DEVICE_TYPES,
  BREAKPOINTS,
  WIDGET_DIMENSIONS,
  getDeviceType,
  getWidgetDimensions,
  getResponsiveOpenWidth,
  getResponsiveOpenHeight,
  supportsDvh,
  MESSAGE_TYPES,
};
